import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';
import { dispensarizationsParameters } from '../../hooks/components/useDispensarization/constants';
import { DispensarizationsCurrent } from '../../models/Dispensarization/Dispensarization';

export interface DispensarizationsState {
  items: DispensarizationsCurrent[];
  totalItemsCount: number;
  status: Status;
}

export interface DispensarizationsResponse {
  items: DispensarizationsCurrent[];
  totalItemsCount: number;
}

export interface DispensarizationsRequest {
  'Filter.From': string;
  'Filter.To': string;
  PageIndex: number;
  PageSize: number;
  SortKeySelector: string;
  'Filter.ClinicIds': string[];
  'Filter.BatchIds': string[];
  'Filter.Result': string[];
  'Filter.States': string[];
  'Filter.AttemptStates': string[];
  'Filter.TextSearch': string;
  SortOrder: string;
}

const initialState: DispensarizationsState = {
  items: [],
  totalItemsCount: 0,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getDispensarizations = createAsyncThunk(
  'Dispensarizations/getDispensarizations',
  async (request: DispensarizationsRequest, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<DispensarizationsResponse>(
        'task/dispensarization',
        {
          params: {
            [dispensarizationsParameters['Filter.From']]:
              request['Filter.From'],
            [dispensarizationsParameters['Filter.To']]: request['Filter.To'],
            [dispensarizationsParameters.PageIndex]: request.PageIndex,
            [dispensarizationsParameters.PageSize]: request.PageSize,
            [dispensarizationsParameters.SortKeySelector]:
              request.SortKeySelector,
            [dispensarizationsParameters.SortOrder]: request.SortOrder,
            [dispensarizationsParameters['Filter.ClinicIds']]:
              request['Filter.ClinicIds'],
            [dispensarizationsParameters['Filter.BatchIds']]:
              request['Filter.BatchIds'],
            [dispensarizationsParameters['Filter.Result']]:
              request['Filter.Result'],
            [dispensarizationsParameters['Filter.States']]:
              request['Filter.States'],
            [dispensarizationsParameters['Filter.AttemptStates']]:
              request['Filter.AttemptStates'],
            [dispensarizationsParameters['Filter.TextSearch']]:
              request['Filter.TextSearch'],
          },
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue(
          'Не удалось получить список исходящих звонков по диспансеризации!',
        );
    }
  },
);

export const dispensarizationsSlice = createSlice({
  name: 'dispensarizations',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getDispensarizations.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getDispensarizations.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.items = action.payload.items;
        state.totalItemsCount = action.payload.totalItemsCount;
        return state;
      })
      .addCase(getDispensarizations.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить звонки';
        return state;
      });
  },
});

export default dispensarizationsSlice.reducer;

export const selectDispensarizations = (state: RootState) =>
  state.dispensarizations.items;

export const totalDispensarizations = (state: RootState) =>
  state.dispensarizations.totalItemsCount;

export const dispensarizationsStatus = (state: RootState) =>
  state.dispensarizations.status;
